import { NgZone } from '@angular/core';
import { EventDispatcher, Intersection, Mesh, MeshBasicMaterial, Object3D, Raycaster, Vector2 } from 'three';
import { Vector3 } from 'three';
import { MouseButton } from './../load-actions';
import { CameraBox } from './camerabox';
import CameraBoxRenderingContext from './cameraboxrenderingcontext';
import { MainView } from '../main-view';

interface CameraBoxEvent {
  type: 'cameraboxchange';
  object: Vector3;
}

export class CameraBoxManager extends EventDispatcher<{ cameraboxchange: CameraBoxEvent }> {
  public cameraBoxRenderingContext: CameraBoxRenderingContext;
  public cameraBox: CameraBox;
  public mouse = new Vector2();
  public cameraPosition = new Vector3();
  public hoveredElement: Mesh;
  private mainView: MainView;

  constructor(private container: HTMLElement, private zone: NgZone, mainView: MainView) { // Add mainView to constructor
    super();
    this.mainView = mainView;
    this.zone.runOutsideAngular(() => {
      const cameraBoxContainer = document.createElement('div');
      cameraBoxContainer.setAttribute('style', 'position: absolute; top: 30px; right: 60px');
      this.container.appendChild(cameraBoxContainer);
      this.cameraBoxRenderingContext = this.createCameraBoxRenderingContext(cameraBoxContainer);
      this.addEventListeners();
      this.listenToCameraBoxChange();
      this.drawCameraBox();
      this.animate();
    });
  }

  public syncCameraBox(direction: Vector3) {
    this.setCameraBoxDirection(direction);
  }

  private addEventListeners() {
    this.cameraBoxRenderingContext.renderer.domElement.addEventListener(
      'pointermove',
      event => this.onMouseMove(event),
      false,
    );
    this.cameraBoxRenderingContext.renderer.domElement.addEventListener(
      'pointerdown',
      event => this.onMouseClick(event),
      false,
    );

    this.cameraBoxRenderingContext.cameraControls.addEventListener('start', () => {
      this.mainView.startAnimation();
    });
    this.cameraBoxRenderingContext.cameraControls.addEventListener('end', () => {
      this.mainView.stopAnimation();
    });
  }

  private createCameraBoxRenderingContext(domElement: HTMLElement): CameraBoxRenderingContext {
    let camera;
    this.zone.runOutsideAngular(() => {
      camera = CameraBoxRenderingContext.get(domElement);
    });
    return camera as CameraBoxRenderingContext;
  }

  private drawCameraBox() {
    this.cameraBox = new CameraBox(this.cameraBoxRenderingContext.scene);
  }

  private onMouseMove = (event: MouseEvent) => {
    this.mouse.x = (event.offsetX / 130) * 2 - 1;
    this.mouse.y = -(event.offsetY / 130) * 2 + 1;
    const raycaster = new Raycaster();
    raycaster.setFromCamera(this.mouse, this.cameraBoxRenderingContext.camera);
    const intersects = raycaster.intersectObjects(this.cameraBox.hoverableObjects.children, true);
    this.cameraBox.hoverableObjects.children.forEach((object: Mesh) => {
      (object.material as MeshBasicMaterial).color.setHex(0xf8f8f8);
    });
    if (intersects[0]) {
      document.body.style.cursor = 'pointer';
      this.cameraPosition = this.recognizeFacePart(this.getHoveredElement(intersects));
    } else {
      this.cameraPosition = undefined;
      document.body.style.cursor = 'initial';
    }
  }

  private onMouseClick(event: MouseEvent) {
    event.preventDefault();
    if (this.cameraPosition && event.button === MouseButton.Left) {
      this.setCameraBoxDirection(this.cameraPosition);
    }
    this.onMouseMove(event);
  }

  private getHoveredElement(intersects: Intersection[]) {
    const closestElements = intersects.filter(
      element => element.distance === intersects[0].distance,
    );
    this.hoveredElement = closestElements[0].object as Mesh;
    (this.hoveredElement.material as MeshBasicMaterial).color.setHex(0xd9efff);
    return this.hoveredElement;
  }

  private recognizeFacePart(object: Object3D) {
    const position = new Vector3();
    object.getWorldPosition(position);
    return position;
  }

  private setCameraBoxDirection(direction: Vector3) {
    this.zone.runOutsideAngular(() => {
      const distance = this.cameraBoxRenderingContext.camera.position.clone().length();
      this.cameraBoxRenderingContext.camera.position.copy(
        direction
          .clone()
          .normalize()
          .multiplyScalar(distance),
      );
      this.cameraBoxRenderingContext.cameraControls.update();
    });
  }

  private listenToCameraBoxChange() {
    let lol = false;
    this.cameraBoxRenderingContext.cameraControls.addEventListener('start', () => {
      lol = true;
    });
    this.cameraBoxRenderingContext.cameraControls.addEventListener('end', () => {
      lol = false;
    });
    this.cameraBoxRenderingContext.cameraControls.addEventListener('change', () => {
      if (lol) {
        this.dispatchEvent({
          type: 'cameraboxchange',
          object: this.cameraBoxRenderingContext.camera.position.clone(),
        });
      }
    });
  }

  private render() {
    this.cameraBoxRenderingContext.renderer.render(
      this.cameraBoxRenderingContext.scene,
      this.cameraBoxRenderingContext.camera,
    );
  }

  private animate = () => {
    this.render();
    requestAnimationFrame(this.animate);
  }
}
